/**
 * 租户自助注册（文档 3.1 注册阶段）
 *
 * 注册成功后租户为「待认证」状态：平台运营审核营业执照（认证）后才算正式开通。
 * 表单校验规则与后端 zod schema 保持一致，避免提交后才报错。
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Descriptions, Form, Input, Result, Space, Steps, Typography } from 'antd';
import { SafetyCertificateOutlined } from '@ant-design/icons';
import type { TenantRegisterRequest, TenantRegisterResponse } from '@ilc/shared';
import { authApi } from '@/api/endpoints';
import { PageHeader } from '@/components/PageHeader';

interface RegisterFormValues extends TenantRegisterRequest {
  confirmPassword: string;
}

export default function Register(): React.ReactElement {
  const [form] = Form.useForm<RegisterFormValues>();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TenantRegisterResponse | null>(null);

  const handleSubmit = async (values: RegisterFormValues): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      const payload: TenantRegisterRequest = {
        companyName: values.companyName.trim(),
        creditCode: values.creditCode.trim().toUpperCase(),
        // 租户标识必填，不再有"留空由平台生成"那条路
        tenantCode: values.tenantCode.trim(),
        contactName: values.contactName.trim(),
        contactPhone: values.contactPhone.trim(),
        adminUsername: values.adminUsername.trim(),
        adminPassword: values.adminPassword,
        ...(values.contactEmail ? { contactEmail: values.contactEmail.trim() } : {}),
      };
      const res = await authApi.register(payload);
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '注册失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    /**
     * 注册成功页。
     *
     * 【2026-09-17 起注册即开通】企业名称与统一社会信用代码在提交时已由企查查核验，
     * 核验通过就地建好角色/流程/配额/管理员并置为可用，**不再有"等待平台审核"这一步**。
     * 页面文案必须跟着改：从前那句"认证通过前可先登录熟悉界面"其实做不到——
     * 那时 `app_user` 还没建，账号根本不存在（登录只会报账号密码不正确）。
     */
    return (
      <div className="ilc-login-bg">
        <Card style={{ maxWidth: 640, width: '100%' }}>
          <Result
            status="success"
            title="注册成功，租户已开通"
            subTitle={result.message}
            extra={[
              <Button type="primary" key="login" onClick={() => navigate('/login')}>
                去登录
              </Button>,
            ]}
          />
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="企业名称">{form.getFieldValue('companyName')}</Descriptions.Item>
            <Descriptions.Item label="租户标识">
              <Typography.Text code copyable>
                {result.tenantCode}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="当前状态">
              {result.status === 'ACTIVE' ? '已开通（企业信息已通过企查查核验）' : result.status}
            </Descriptions.Item>
            <Descriptions.Item label="管理员账号">{form.getFieldValue('adminUsername')}</Descriptions.Item>
          </Descriptions>
          <Alert
            style={{ marginTop: 16 }}
            type="info"
            showIcon
            message="下一步"
            description="用上面这个管理员账号和您刚设置的密码登录即可开始使用。登录后建议先到「用户管理」给同事开账号、到「外部接口配置」核对接口状态。"
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="ilc-login-bg" style={{ alignItems: 'flex-start', paddingTop: 40 }}>
      <div style={{ maxWidth: 860, width: '100%' }}>
        <Card>
          <PageHeader
            title={
              <Space>
                <SafetyCertificateOutlined style={{ color: '#1677ff' }} />
                企业自助注册
              </Space>
            }
            /* 【2026-09-17 起文案同步】注册改为「企查查核验通过即开通」，
               从前那句"由平台审核企业资质…认证通过即开通"以及步骤条里的
               「平台审核认证」都不再成立——新租户不会再停在待认证。 */
            description="提交后系统会用企查查核验企业名称与统一社会信用代码；核验通过即开通租户空间并初始化角色、流程与权限模板，用你设置的密码即可登录。"
            extra={<Link to="/login">已有账号，去登录</Link>}
          />

          <Steps
            size="small"
            current={0}
            style={{ marginBottom: 20 }}
            items={[
              { title: '填写企业信息', description: '当前步骤' },
              { title: '系统核验企业', description: '企查查比对名称与信用代码' },
              { title: '即时开通', description: '初始化角色/流程/权限' },
              { title: '登录后配置', description: '套餐、配额与账套' },
            ]}
          />

          {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} closable onClose={() => setError(null)} /> : null}

          <Form<RegisterFormValues>
            form={form}
            layout="vertical"
            requiredMark
            onFinish={(values) => void handleSubmit(values)}
            scrollToFirstError
          >
            <Typography.Title level={5}>企业信息</Typography.Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0 16px' }}>
              <Form.Item
                name="companyName"
                label="企业名称"
                rules={[{ required: true, message: '请输入企业名称' }, { min: 2, max: 200, message: '企业名称 2-200 个字' }]}
              >
                <Input placeholder="与营业执照保持一致" />
              </Form.Item>

              <Form.Item
                name="creditCode"
                label="统一社会信用代码"
                rules={[
                  { required: true, message: '请输入统一社会信用代码' },
                  { len: 18, message: '统一社会信用代码应为 18 位' },
                  { pattern: /^[0-9A-HJ-NPQRTUWXY]{18}$/i, message: '包含非法字符，请核对营业执照' },
                ]}
              >
                <Input placeholder="18 位，如 91370100MA3D9K8X2P" maxLength={18} className="ilc-mono" />
              </Form.Item>

              <Form.Item
                name="tenantCode"
                label="租户标识"
                /* 2026-09-23 起必填：从前留空由平台生成随机码，企业自己不知道标识是什么、
                   登录时要问客服，而这个标识发出去就改不回来。让注册人当场自己定。 */
                extra="登录时要用它，定了不能改；仅支持字母、数字、下划线、短横线"
                rules={[
                  { required: true, message: '请填写租户标识（登录时要用）' },
                  { pattern: /^[A-Za-z0-9_-]{4,64}$/, message: '长度 4-64，仅字母/数字/下划线/短横线' },
                ]}
              >
                <Input placeholder="必填，如 QDZY，一般为企业拼音缩写" maxLength={64} />
              </Form.Item>
            </div>

            <Typography.Title level={5} style={{ marginTop: 8 }}>
              联系人
            </Typography.Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0 16px' }}>
              <Form.Item name="contactName" label="联系人" rules={[{ required: true, message: '请填写联系人' }]}>
                <Input placeholder="姓名" maxLength={64} />
              </Form.Item>
              <Form.Item
                name="contactPhone"
                /* 标签从「联系电话」改为「手机号」：这一项只收 11 位手机号（座机填不进来），
                   叫"电话"会让人试着填 0531-xxxxxxx 然后被格式校验挡住。
                   短信验证码校验在下一轮补（需先申请阿里云【验证码】类短信模板）。 */
                label="手机号"
                rules={[
                  { required: true, message: '请填写手机号' },
                  { pattern: /^1[3-9]\d{9}$/, message: '请填写 11 位手机号' },
                ]}
              >
                <Input placeholder="用于认证与业务通知" maxLength={11} />
              </Form.Item>
              <Form.Item name="contactEmail" label="邮箱（选填）" rules={[{ type: 'email', message: '邮箱格式不正确' }]}>
                <Input placeholder="接收接口异常与待办提醒" maxLength={128} />
              </Form.Item>
            </div>

            <Typography.Title level={5} style={{ marginTop: 8 }}>
              管理员账号
            </Typography.Title>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0 16px' }}>
              <Form.Item
                name="adminUsername"
                label="管理员账号"
                extra="以字母开头，由字母、数字、下划线组成，长度 4-64"
                rules={[
                  { required: true, message: '请输入管理员账号' },
                  { pattern: /^[A-Za-z][A-Za-z0-9_]{3,63}$/, message: '以字母开头，字母/数字/下划线，长度 4-64' },
                ]}
              >
                <Input placeholder="如 admin" autoComplete="off" />
              </Form.Item>

              <Form.Item
                name="adminPassword"
                label="管理员密码"
                extra="至少 8 位，需同时包含字母与数字"
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 8, max: 72, message: '密码长度 8-72 位' },
                  { pattern: /[a-zA-Z]/, message: '密码需包含字母' },
                  { pattern: /\d/, message: '密码需包含数字' },
                ]}
              >
                <Input.Password placeholder="如 Tenant@123456" autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="确认密码"
                dependencies={['adminPassword']}
                rules={[
                  { required: true, message: '请再次输入密码' },
                  ({ getFieldValue }) => ({
                    validator(_rule, value) {
                      if (!value || getFieldValue('adminPassword') === value) return Promise.resolve();
                      return Promise.reject(new Error('两次输入的密码不一致'));
                    },
                  }),
                ]}
              >
                <Input.Password placeholder="再次输入密码" autoComplete="new-password" />
              </Form.Item>
            </div>

            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="平台会为每个租户初始化 6 个默认角色（租户管理员、项目部经办人、财务初审岗、财务记账岗、法务岗、技术实施组），可在「角色权限」中按需增删改。"
            />

            <Space>
              <Button type="primary" htmlType="submit" loading={submitting}>
                提交注册
              </Button>
              <Button onClick={() => navigate('/login')}>返回登录</Button>
            </Space>
          </Form>
        </Card>
      </div>
    </div>
  );
}
